import * as THREE from 'three'
import { debounce } from './utils'
import { BackgroundOptions } from './types'
import PostEffect from './PostEffect'
import Ground from './Ground'
import TitleObject from './TitleObject'
import SheildObject from './ShieldObject'
import SkyOctahedronShell from './SkyOctahedronShell'
import Particles from './Particles'
import SmoothScrollManager from './utils/SmoothScrollManager'

// eslint-disable-next-line prettier/prettier
const afterSmoothScrollManagerRenderHandlers: Array<((y: number) => void) | null> = []

// TODO: create a handleAfterSmoothScrollManagerRender
export function handleAfterSmoothScrollManagerRender(y: number): void {
  afterSmoothScrollManagerRenderHandlers.forEach((handler) => {
    if (handler) {
      handler(y)
    }
  })
}
export function addAfterSmoothScrollManagerRender(
  handler: (y: number) => void
): number {
  afterSmoothScrollManagerRenderHandlers.push(handler)
  return afterSmoothScrollManagerRenderHandlers.length - 1
}
export function removeAfterSmoothScrollManagerRender(id: number): void {
  if (afterSmoothScrollManagerRenderHandlers[id]) {
    afterSmoothScrollManagerRenderHandlers[id] = null
  }
}

export default function initBackground(options: BackgroundOptions): void {
  const scrollManager = new SmoothScrollManager()

  const canvas = options.$canvas
  const renderer = new THREE.WebGL1Renderer({
    antialias: false,
    canvas,
  })
  const renderBack = new THREE.WebGLRenderTarget(
    document.body.clientWidth,
    window.innerHeight
  )
  const scene = new THREE.Scene()
  const sceneBack = new THREE.Scene()
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  const cameraBack = new THREE.PerspectiveCamera(
    45,
    document.body.clientWidth / window.innerHeight,
    1,
    10000
  )
  const clock = new THREE.Clock()

  // components
  const titleObject = new TitleObject()
  const sheildObject = new SheildObject()
  // const skyOctahedron = new SkyOctahedron();
  const skyOctahedronShell = new SkyOctahedronShell()
  const skyOctahedronShell2 = new SkyOctahedronShell(1)
  const ground = new Ground()
  const particles = new Particles({
    count: 400,
    size: 10,
    width: 2048,
    height: 2048,
    scaleMin: 0.2,
    insideColor: new THREE.Color('#ff6030'),
    outsideColor: new THREE.Color('#1b3984'),
    canvasHeight: window.innerHeight,
    minFloatHeight: 50,
    maxFloatHeight: 500,
    floatStartZRange: 150,
  })
  // const debris = [
  //    new Debris(400, -500, 200),
  //    new Debris(-350, -600, -50),
  //    new Debris(-150, -700, -150),
  //    new Debris(-500, -900, 0),
  //    new Debris(100, -1100, 250),
  //    new Debris(-100, -1200, -300),
  //    new Debris(150, -1500, -100),
  // ];
  const postEffect = new PostEffect(renderBack.texture)

  const resizeWindow = (): void => {
    canvas.width = document.body.clientWidth
    canvas.height = window.innerHeight
    cameraBack.aspect = document.body.clientWidth / window.innerHeight
    cameraBack.updateProjectionMatrix()
    renderBack.setSize(document.body.clientWidth, window.innerHeight)
    renderer.setSize(document.body.clientWidth, window.innerHeight)
    postEffect.resize()
  }

  const render = (): void => {
    const time = clock.getDelta()
    titleObject.render(time)
    // skyOctahedron.render(time)
    skyOctahedronShell.render(time)
    sheildObject.render(time)
    skyOctahedronShell2.render(time)
    ground.render(time)
    particles.render(time)
    // for (var i = 0; i < debris.length; i++) {
    //   debris[i].render(time);
    // }
    renderer.setRenderTarget(renderBack)
    renderer.render(sceneBack, cameraBack)
    postEffect.render(time)
    renderer.setRenderTarget(null)
    renderer.render(scene, camera)
  }

  const renderLoop = (): void => {
    render()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    requestAnimationFrame(renderLoop)
  }

  const bind = (): void => {
    window.addEventListener(
      'resize',
      debounce(() => {
        resizeWindow()
      }, 1000)
    )

    scrollManager.renderNext = (y: number) => {
      if (scrollManager.isValidSmooth()) {
        cameraBack.position.y = scrollManager.hookes.contents.velocity[1] * 0.6
      } else {
        cameraBack.position.y = scrollManager.scrollTop * -1
      }
      handleAfterSmoothScrollManagerRender(y)
    }
  }

  const init = (): void => {
    renderer.setSize(document.body.clientWidth, window.innerHeight)
    renderer.setClearColor(options.backgroundColor, 1.0)
    cameraBack.position.z = 800

    scene.add(postEffect.obj)
    titleObject.loadTexture(() => {
      sceneBack.add(titleObject.obj)
      sceneBack.add(skyOctahedronShell.obj)
      sceneBack.add(sheildObject.obj)
      // // sceneBack.add(skyOctahedron.obj);
      sceneBack.add(skyOctahedronShell2.obj)
      sceneBack.add(ground.obj)
      sceneBack.add(particles.obj)
      // for (var i = 0; i < debris.length; i++) {
      //   sceneBack.add(debris[i].obj);
      // }
      // transitionOnload();
    })

    clock.start()

    bind()
    resizeWindow()
    renderLoop()
    scrollManager.start()
  }
  init()
}
